/**
 * bank-lookup.ts — KP id → JSON bank URL mapping for the standalone entry.
 *
 * Mirrors the per-subject lookup tables in math-lesson.ts / chinese-lesson.ts
 * / english-lesson.ts / nature-lesson.ts / social-lesson.ts but lives in the
 * entry namespace so we do not transitively import quiz/overlay.ts,
 * mascot, etc.
 *
 * If a KP id has no bank entry it is treated as parametric — the entry quiz
 * page will fall back to the legacy lesson runner OR show a friendly
 * "本單元為遊戲端動態題庫,請在遊戲首頁練習" message. (Phase 4 keeps it
 * read-only: we do not run parametric generators in the entry; they live
 * in the game-side runner.)
 */

const BANK_URL: Record<string, string> = {
  // ── math ──────────────────────────────────────────────────────────────
  'math.fraction.basic': '/data/math-fraction-basic-康軒.json',
  'math.length.cm': '/data/math-length-康軒.json',
  'math.weight.kg': '/data/math-weight-康軒.json',
  'math.angle.basic': '/data/math-angle-康軒.json',
  'math.shape.rectangle': '/data/math-shape-rectangle-康軒.json',
  'math.shape.circle': '/data/math-shape-circle-康軒.json',
  'math.stats.bar': '/data/math-stats-bar-康軒.json',
  'math.stats.table': '/data/math-table-康軒.json',
  'math.frac.unit': '/data/math-fraction-intro-康軒.json',
  'math.mul.2x1.meow911': '/data/meow911-math-ch2-mul.json',
  'math.div.remainder.meow911': '/data/meow911-math-ch3-div.json',
  'math.fraction.basic.meow911': '/data/meow911-math-ch4-fraction.json',
  'math.decimal.basic.meow911': '/data/meow911-math-ch5-decimal.json',
  'math.area.rectangle.meow911': '/data/meow911-math-ch6-area.json',
  'math.time.clock.meow911': '/data/meow911-math-ch7-time.json',
  'math.meow911.misc': '/data/meow911-math-misc-pool.json',
  // (parametric KPs not listed — they have no bank.)

  // ── chinese: theme ────────────────────────────────────────────────────
  'chinese.zhuyin.basic': '/data/chinese-zhuyin-康軒.json',
  'chinese.segment.summary': '/data/chinese-segment-康軒.json',
  'chinese.phonetic.tone': '/data/chinese-phonetic-康軒.json',
  'chinese.vocab.usage': '/data/chinese-vocab-usage-康軒.json',
  'chinese.sentence.pattern': '/data/chinese-sentence-康軒.json',
  'chinese.comprehension.basic': '/data/chinese-read-康軒.json',
  'chinese.writing.guided': '/data/chinese-write-康軒.json',
  'chinese.character.radical': '/data/chinese-radical-康軒.json',
  'chinese.comprehension.explain': '/data/chinese-explain-康軒.json',
  'chinese.writing.diary': '/data/chinese-diary-康軒.json',
  'chinese.punctuation.basic': '/data/chinese-punctuation-康軒.json',
  'chinese.rhetoric.basic': '/data/chinese-rhetoric-康軒.json',
  'chinese.idiom.basic': '/data/chinese-idiom-康軒.json',
  'chinese.culture.community': '/data/chinese-culture-康軒.json',
  // chinese by-lesson L01-L12
  'chinese.l01.xuyuan': '/data/chinese-l01-xuyuan-康軒.json',
  'chinese.l02.xiayushihou': '/data/chinese-l02-xiayushihou-康軒.json',
  'chinese.l03.yujianmeiruunainai': '/data/chinese-l03-yujianmeiruunainai-康軒.json',
  'chinese.l04.gongjiangzhizu': '/data/chinese-l04-gongjiangzhizu-康軒.json',
  'chinese.l05.xuetianshukai': '/data/chinese-l05-xuetianshukai-康軒.json',
  'chinese.l06.shenqimima': '/data/chinese-l06-shenqimima-康軒.json',
  'chinese.l07.youtonghua': '/data/chinese-l07-youtonghua-康軒.json',
  'chinese.l08.daziranmeishuguan': '/data/chinese-l08-daziranmeishuguan-康軒.json',
  'chinese.l09.taiwandeshanjiaoyu': '/data/chinese-l09-taiwandeshanjiaoyu-康軒.json',
  'chinese.l10.yufuhejinyu': '/data/chinese-l10-yufuhejinyu-康軒.json',
  'chinese.l11.congmingdeshulu': '/data/chinese-l11-congmingdeshulu-康軒.json',
  'chinese.l12.haiyaodiejici': '/data/chinese-l12-haiyaodiejici-康軒.json',

  // ── english ───────────────────────────────────────────────────────────
  'english.alphabet.phonics': '/data/english-alphabet-翰林.json',
  'english.vocab.family': '/data/english-vocab-family-翰林.json',
  'english.phonics.basic': '/data/english-phonics-翰林.json',
  'english.vocab.school': '/data/english-vocab-school-翰林.json',
  'english.vocab.colors': '/data/english-colors-翰林.json',
  'english.dialogue.daily': '/data/english-greetings-翰林.json',
  'english.vocab.food': '/data/english-vocab-food-翰林.json',
  'english.vocab.animals': '/data/english-vocab-animals-翰林.json',
  'english.numbers.basic': '/data/english-numbers-翰林.json',
  'english.numbers.ordinal': '/data/english-numbers-ordinal-翰林.json',
  'english.grammar.questions': '/data/english-grammar-questions-翰林.json',
  'english.grammar.statements': '/data/english-grammar-statements-翰林.json',
  'english.dialogue.classroom': '/data/english-dialogue-classroom-翰林.json',

  // ── nature ────────────────────────────────────────────────────────────
  'nature.water.states': '/data/nature-water-states-南一.json',
  'nature.plant.lifecycle': '/data/nature-plant-lifecycle-南一.json',
  'nature.animal.classify': '/data/nature-animals-南一.json',
  'nature.sound.light.basic': '/data/nature-sound-light-南一.json',
  'nature.machine.basic': '/data/nature-force-南一.json',
  'nature.weather.seasons': '/data/nature-weather-南一.json',
  'nature.rock.basic': '/data/nature-rock-南一.json',
  'nature.plant.growth': '/data/nature-plant-growth-南一.json',
  'nature.animal.adapt': '/data/nature-animal-adapt-南一.json',
  'nature.water.temp': '/data/nature-water-temp-南一.json',
  'nature.weather.measure': '/data/nature-weather-measure-南一.json',
  'nature.magnet.basic': '/data/nature-magnet-南一.json',
  'nature.force.deform': '/data/nature-force-deform-南一.json',
  'nature.light.basic': '/data/nature-light-basic-南一.json',

  // ── social ────────────────────────────────────────────────────────────
  'social.community.basic': '/data/social-community-翰林.json',
  'social.taiwan.geography': '/data/social-taiwan-geography-翰林.json',
  'social.hometown.basic': '/data/social-hometown-翰林.json',
  'social.custom.festival': '/data/social-festival-翰林.json',
  'social.life.rule': '/data/social-life-rule-翰林.json',
  'social.traffic.basic': '/data/social-transport-翰林.json',
  'social.environment.basic': '/data/social-environment-翰林.json',
  'social.family.roles': '/data/social-family-roles-翰林.json',
  'social.local.environment': '/data/social-local-environment-翰林.json',
};

export function getBankUrl(kpId: string): string | null {
  return BANK_URL[kpId] ?? null;
}

/** All KP ids that have a JSON bank entry. */
export function listBankKps(): readonly string[] {
  return Object.keys(BANK_URL);
}
